import React from 'react';

const steps = [
  {
    number: "01",
    title: "Application & Screening",
    content: (
      <>
        <p className="mb-4">
          Each applicant undergoes a structured evaluation to assess technical readiness, problem-solving ability, and alignment with current project requirements.
        </p>
        <p className="mb-4">
          Admission is merit-based and capacity-governed.
          <br />
          Applicants who do not meet minimum contribution standards may be deferred or declined for the current cycle.
        </p>
        <p>
          Screening ensures that accepted participants can operate effectively within the governed development environment.
        </p>
      </>
    )
  },
  {
    number: "02",
    title: "Tier Allocation",
    content: (
      <>
        <p className="mb-4">
          Admitted participants are assigned to projects aligned with their demonstrated capability level.
        </p>
        <p className="mb-2 font-medium text-slate-900">Placement is determined by:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Technical assessment outcomes</li>
          <li>Project complexity</li>
          <li>Mentor bandwidth</li>
          <li>Active development requirements</li>
        </ul>
        <p>
          Allocation reflects readiness — not preference alone.
        </p>
      </>
    )
  },
  {
    number: "03",
    title: "Structured Contribution Cycles",
    content: (
      <>
        <p className="mb-4">
          Work is executed within defined sprint intervals under mentor supervision.
        </p>
        <p className="mb-2 font-medium text-slate-900">Participants are expected to:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Deliver assigned tasks within timelines</li>
          <li>Maintain documentation discipline</li>
          <li>Adhere to architectural standards</li>
          <li>Collaborate within version-controlled workflows</li>
        </ul>
        <p>
          Contribution is structured and performance-tracked.
        </p>
      </>
    )
  },
  {
    number: "04",
    title: "Review & Governance",
    content: (
      <>
        <p className="mb-4">
          All submissions undergo review against defined quality benchmarks.
        </p>
        <p className="mb-2 font-medium text-slate-900">Evaluation includes:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Code quality</li>
          <li>Documentation standards</li>
          <li>Communication discipline</li>
          <li>Collaboration behavior</li>
          <li>Adherence to project architecture</li>
        </ul>
        <p>
          Performance is recorded. Feedback is documented. Standards are enforced.
        </p>
      </>
    )
  },
  {
    number: "05",
    title: "Credential & Record Issuance",
    content: (
      <>
        <p className="mb-4">
          Internship certification reflects verified participation within the governed contribution framework.
        </p>
        <p className="mb-4">
          Credential issuance is not time-based.
          <br />
          It reflects measurable engagement, adherence to standards, and documented contribution history.
        </p>
        <p>
          Records will include structured contribution summaries and performance validation.
        </p>
      </>
    )
  }
];

const ParticipationFramework = () => {
  return (
    <section id="framework" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-16">Participation Framework</h2>
        
        <div className="relative border-l-2 border-slate-200 ml-4 md:ml-0 space-y-12">
          {steps.map((step, index) => (
            <div key={index} className="relative pl-12 md:pl-24 group">
              {/* Marker */}
              <div className="absolute -left-[9px] top-0 w-4 h-4 bg-white border-2 border-slate-300 rounded-full group-hover:border-slate-900 group-hover:scale-110 transition-all"></div>
              
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="md:w-1/4">
                  <span className="text-4xl font-bold text-slate-200 group-hover:text-slate-900 transition-colors">{step.number}</span>
                </div>
                <div className="md:w-3/4">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{step.title}</h3>
                  <div className="text-slate-600 leading-relaxed text-base">
                    {step.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ParticipationFramework;
